/**
 * Secure File Upload Service
 * Client-side utility for uploading files through the secure backend endpoint
 */

import { getAuth } from 'firebase/auth';

export type FileType = 'channel-image' | 'profile-picture' | 'company-logo' | 'cv';

interface UploadOptions {
  fileType: FileType;
  channelUsername?: string;
  onProgress?: (progress: number) => void;
}

interface UploadResult {
  success: boolean;
  downloadURL?: string;
  filename?: string;
  error?: string;
}

/**
 * Validates file on client-side before upload (basic checks)
 * Note: Server-side validation is still required and enforced
 */
function validateFileClientSide(file: File, fileType: FileType): { valid: boolean; error?: string } {
  // Check file size limits
  const maxSizes: Record<FileType, number> = {
    'channel-image': 5 * 1024 * 1024, // 5MB
    'profile-picture': 5 * 1024 * 1024, // 5MB
    'company-logo': 5 * 1024 * 1024, // 5MB
    'cv': 10 * 1024 * 1024, // 10MB
  };

  const maxSize = maxSizes[fileType];
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds ${maxSize / (1024 * 1024)}MB limit`,
    };
  }

  // Check allowed MIME types
  const allowedTypes: Record<FileType, string[]> = {
    'channel-image': ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    'profile-picture': ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    'company-logo': ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    'cv': ['application/pdf'],
  };

  const allowed = allowedTypes[fileType];
  if (!allowed.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${allowed.join(', ')}`,
    };
  }

  // Check dangerous extensions
  const dangerousExtensions = [
    'exe', 'dll', 'bat', 'cmd', 'sh', 'ps1', 'vbs', 'js', 'mjs', 'cjs',
    'jar', 'app', 'html', 'htm', 'svg', 'xml', 'php', 'jsp', 'asp',
  ];

  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension && dangerousExtensions.includes(extension)) {
    return {
      valid: false,
      error: 'File type not allowed for security reasons',
    };
  }

  return { valid: true };
}

/**
 * Validates file magic bytes on client-side before upload
 * Provides immediate feedback without server round-trip
 */
async function validateFileMagicBytes(file: File, fileType: FileType): Promise<{ valid: boolean; error?: string }> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);
      
      // Check magic bytes for common formats
      const magicBytes: Record<string, number[][]> = {
        'jpeg': [[0xFF, 0xD8, 0xFF]],
        'png': [[0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]],
        'gif': [[0x47, 0x49, 0x46, 0x38, 0x37, 0x61], [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]],
        'webp': [[0x52, 0x49, 0x46, 0x46]],
        'pdf': [[0x25, 0x50, 0x44, 0x46]],
      };
      
      // For images, check valid signature
      if (fileType !== 'cv') {
        let hasValidSignature = false;
        
        for (const [format, signatures] of Object.entries(magicBytes)) {
          if (format === 'pdf') continue;
          
          for (const signature of signatures) {
            if (bytes.length < signature.length) continue;
            let matches = true;
            for (let i = 0; i < signature.length; i++) {
              if (bytes[i] !== signature[i]) {
                matches = false;
                break;
              }
            }
            if (matches) {
              hasValidSignature = true;
              break;
            }
          }
          if (hasValidSignature) break;
        }
        
        if (!hasValidSignature) {
          resolve({
            valid: false,
            error: 'Security Alert: This file is not a valid image. The file content does not match any supported image format.',
          });
          return;
        }
      } else {
        // For PDFs
        const pdfSignature = magicBytes.pdf[0];
        if (bytes.length < pdfSignature.length) {
          resolve({ valid: false, error: 'File is too small to be valid' });
          return;
        }
        let matches = true;
        for (let i = 0; i < pdfSignature.length; i++) {
          if (bytes[i] !== pdfSignature[i]) {
            matches = false;
            break;
          }
        }
        
        if (!matches) {
          resolve({
            valid: false,
            error: 'Security Alert: This file is not a valid PDF.',
          });
          return;
        }
      }
      
      resolve({ valid: true });
    };
    
    reader.onerror = () => {
      resolve({ valid: false, error: 'Failed to read file' });
    };
    
    // Read first 8KB
    reader.readAsArrayBuffer(file.slice(0, 8192));
  });
}

/**
 * Uploads a file through the secure backend endpoint
 * This ensures all files are validated server-side before storage
 */
export async function secureUploadFile(
  file: File,
  options: UploadOptions
): Promise<UploadResult> {
  try {
    // Client-side validation
    const validation = validateFileClientSide(file, options.fileType);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      };
    }
    // Magic byte validation (client-side)
    const magicByteValidation = await validateFileMagicBytes(file, options.fileType);
    if (!magicByteValidation.valid) {
      return {
        success: false,
        error: magicByteValidation.error,
      };
    }
    
    // Get authentication token
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      return {
        success: false,
        error: 'User must be authenticated to upload files',
      };
    }

    const idToken = await user.getIdToken();

    // Prepare form data
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', options.fileType);
    
    if (options.channelUsername) {
      formData.append('channelUsername', options.channelUsername);
    }

    // Get the Cloud Function URL
    // For production: https://<region>-<project-id>.cloudfunctions.net/secureFileUpload
    // For development: http://localhost:5001/<project-id>/<region>/secureFileUpload
    const isDevelopment = window.location.hostname === 'localhost';
    const projectId = 'nibjobs-dev'; // Replace with your actual project ID
    const region = 'us-central1'; // Replace with your actual region
    
    const functionUrl = isDevelopment
      ? `http://localhost:5001/${projectId}/${region}/secureFileUpload`
      : `https://${region}-${projectId}.cloudfunctions.net/secureFileUpload`;

    // Upload with progress tracking
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      // Provide user-friendly security messages
      const serverError = result.error || '';
      let userMessage = serverError;
      
      // Detect specific security issues and provide clear messages
      if (serverError.includes('malicious content') || serverError.includes('potentially malicious')) {
        userMessage = 'Security Alert: This file contains suspicious content and cannot be uploaded for your safety.';
      } else if (serverError.includes('does not match declared file type') || serverError.includes('File content mismatch')) {
        userMessage = 'Security Alert: This file appears to be disguised as a different file type and has been rejected.';
      } else if (serverError.includes('blocked extension') || serverError.includes('not allowed')) {
        userMessage = 'This file type is not allowed. Please upload an image (JPG, PNG, GIF) or PDF only.';
      } else if (serverError.includes('exceeds maximum')) {
        userMessage = 'File is too large. Maximum size is 5MB for images and 10MB for PDFs.';
      }
      
      return {
        success: false,
        error: userMessage || `Upload failed with status ${response.status}`,
      };
    }

    return result;

  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed. Please try again.',
    };
  }
}

/**
 * Alternative upload method using Firebase callable function
 * Useful when XMLHttpRequest/fetch with custom headers is not available
 */
export async function secureUploadFileCallable(
  file: File,
  options: UploadOptions
): Promise<UploadResult> {
  try {
    // Client-side validation
    const validation = validateFileClientSide(file, options.fileType);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      };
    }

    // Magic byte validation (client-side)
    const magicByteValidation = await validateFileMagicBytes(file, options.fileType);
    if (!magicByteValidation.valid) {
      return {
        success: false,
        error: magicByteValidation.error,
      };
    }

    // Convert file to base64
    const fileData = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Import functions dynamically to avoid issues
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const uploadFunction = httpsCallable(functions, 'secureFileUploadCallable');

    const result = await uploadFunction({
      fileData,
      filename: file.name,
      mimeType: file.type,
      fileType: options.fileType,
      channelUsername: options.channelUsername,
    });

    return result.data as UploadResult;

  } catch (error) {
    console.error('Upload error:', error);
    
    // Provide user-friendly error messages
    let userMessage = 'Upload failed. Please try again.';
    
    if (error instanceof Error) {
      const errorMsg = error.message;
      
      // Detect specific security issues
      if (errorMsg.includes('malicious content') || errorMsg.includes('potentially malicious')) {
        userMessage = 'Security Alert: This file contains suspicious content and cannot be uploaded for your safety.';
      } else if (errorMsg.includes('does not match') || errorMsg.includes('mismatch')) {
        userMessage = 'Security Alert: This file appears to be disguised as a different file type and has been rejected.';
      } else if (errorMsg.includes('blocked') || errorMsg.includes('not allowed')) {
        userMessage = 'This file type is not allowed. Please upload an image (JPG, PNG, GIF) or PDF only.';
      } else if (errorMsg.includes('exceeds') || errorMsg.includes('too large')) {
        userMessage = 'File is too large. Maximum size is 5MB for images and 10MB for PDFs.';
      } else {
        userMessage = errorMsg;
      }
    }
    
    return {
      success: false,
      error: userMessage,
    };
  }
}
